// Oracle PL/SQL %ROWTYPE binding extractor.
//
// Scans the buffer for declarations of the form `<varname> <table>%ROWTYPE`
// (or `<varname> <schema>.<table>%ROWTYPE`) that appear BEFORE the cursor,
// and returns a map from the (lowercased) variable name to its target
// schema + table. Consumed by `emitDottedAccess` in `oracleResolver.ts`:
// at `v_emp_row.|salary`, the resolver looks up `v_emp_row` here, then
// emits the columns of the bound table.
//
// Why a text-walk rather than the ANTLR `resolveScopePlSql` result: the
// resolver pipeline path that calls into `emitDottedAccess` is synchronous,
// and the existing PL/SQL grammar's `scope.variables[].typeText` is a raw
// string that we'd have to parse here anyway. Doing it directly off the
// tokenizer keeps the dotted-access fast path allocation-light and avoids
// loading the grammar bundle on every keystroke at a member-access slot.

import { tokenize, type Token } from '../context/tokenizer';

const ENGINE = 'oracle' as const;

/**
 * Identifiers that can legally precede `<table>%ROWTYPE` in the tokenizer's
 * `ident` stream but are NOT user variable names — they belong to PL/SQL
 * routine / declaration scaffolding. The first ident in a `<a> <b>%ROWTYPE`
 * triple must not be one of these or it's a false positive (e.g.
 * `PROCEDURE foo RETURN employees%ROWTYPE` would otherwise bind `RETURN`).
 */
const PLSQL_DECLARATION_SCAFFOLD: ReadonlySet<string> = new Set([
  'CREATE',
  'OR',
  'REPLACE',
  'PROCEDURE',
  'FUNCTION',
  'PACKAGE',
  'BODY',
  'TRIGGER',
  'IS',
  'AS',
  'BEGIN',
  'END',
  'DECLARE',
  'CURSOR',
  'TYPE',
  'SUBTYPE',
  'RETURN',
  'EXCEPTION',
  'WHEN',
  'IF',
  'LOOP',
  'FOR',
  'WHILE',
  'IN',
  'OUT',
  'INOUT',
  'CONSTANT',
  'NOT',
  'NULL',
  'DEFAULT',
]);

export type OracleRowtypeBinding = {
  /** Lowercased target table name. */
  readonly tableName: string;
  /** Lowercased schema qualifier, or `undefined` if the declaration was unqualified. */
  readonly schema: string | undefined;
};

/**
 * Walk the buffer up to `cursorOffset` and collect every `<varname>
 * [<schema>.]<table>%ROWTYPE` declaration. Returns a Map keyed by the
 * lowercased variable name. Bindings declared after the cursor (or inside
 * string literals / comments) are skipped — the tokenizer handles literal
 * / comment skipping for us.
 *
 * The walk is forgiving: it does NOT require the declaration to be in the
 * routine's IS|AS … BEGIN window. A `<v> <t>%ROWTYPE` triple found anywhere
 * before the cursor binds `<v>` to `<t>`. False positives are bounded by
 * `PLSQL_DECLARATION_SCAFFOLD` and the requirement that the second token
 * NOT also be a scaffold keyword (which would shadow legitimate decls).
 */
export function findOracleProceduralRowtypeBindings(
  source: string,
  cursorOffset: number,
): Map<string, OracleRowtypeBinding> {
  const bindings = new Map<string, OracleRowtypeBinding>();
  if (cursorOffset <= 0) return bindings;

  const upper = (t: Token | undefined): string | null => {
    if (!t) return null;
    if (t.type !== 'ident' && t.type !== 'kw') return null;
    return t.text.toUpperCase();
  };

  const tokens = tokenize(source, 0, source.length, ENGINE);
  // Stop at the first token whose start is at/after the cursor — anything
  // later isn't in scope at the cursor's position.
  const visibleTokens: Token[] = [];
  for (const t of tokens) {
    if (t.start >= cursorOffset) break;
    visibleTokens.push(t);
  }

  for (let i = 0; i + 3 < visibleTokens.length; i += 1) {
    const a = visibleTokens[i]!;
    const b = visibleTokens[i + 1]!;
    const sep = visibleTokens[i + 2]!;
    const tail = visibleTokens[i + 3]!;

    if (a.type !== 'ident') continue;

    const aUp = a.text.toUpperCase();
    if (PLSQL_DECLARATION_SCAFFOLD.has(aUp)) continue;

    // Shape A: `<var> <table> % ROWTYPE`
    if (b.type === 'ident' && sep.type === 'op' && sep.text === '%') {
      const tailUp = upper(tail);
      const bUp = b.text.toUpperCase();
      if (
        tailUp === 'ROWTYPE' &&
        !PLSQL_DECLARATION_SCAFFOLD.has(bUp)
      ) {
        bindings.set(a.text.toLowerCase(), {
          tableName: b.text.toLowerCase(),
          schema: undefined,
        });
        i += 3;
        continue;
      }
    }

    // Shape B: `<var> <schema> . <table> % ROWTYPE`
    if (b.type === 'ident' && sep.type === 'dot' && i + 5 < visibleTokens.length) {
      const tableTok = visibleTokens[i + 3]!;
      const pct = visibleTokens[i + 4]!;
      const rowtypeTok = visibleTokens[i + 5]!;
      if (
        tableTok.type === 'ident' &&
        pct.type === 'op' &&
        pct.text === '%' &&
        upper(rowtypeTok) === 'ROWTYPE'
      ) {
        const bUp = b.text.toUpperCase();
        const tUp = tableTok.text.toUpperCase();
        if (!PLSQL_DECLARATION_SCAFFOLD.has(bUp) && !PLSQL_DECLARATION_SCAFFOLD.has(tUp)) {
          bindings.set(a.text.toLowerCase(), {
            tableName: tableTok.text.toLowerCase(),
            schema: b.text.toLowerCase(),
          });
          i += 5;
          continue;
        }
      }
    }
  }

  return bindings;
}
